<template>
  <v-btn
    variant="tonal"
    prepend-icon="playlist_add_check"
    @click="dialog = true"
  >
    Manage Settings
    <v-dialog v-model="dialog" width="1500px" max-width="90vw">
      <v-card>
        <v-card-title class="mt-5 ml-5">
          <v-row>
            Select Parameters
            <v-spacer></v-spacer>
            <v-btn
              variant="text"
              color="primary"
              icon="close"
              class="mr-10"
              @click="dialog = false"
            ></v-btn>
          </v-row>
        </v-card-title>
        <v-card-text class="d-flex flex-column" style="max-height: 80vh">
          <v-alert v-if="!hasQuickParams" type="warning" class="pb-10">
            No parameters are selected
          </v-alert>
          <template v-else>
            <v-row>
              <v-col cols="auto" class="subtitle-1">Selected Parameters</v-col>
            </v-row>
            <v-row>
              <v-col
                v-for="param in quickParams"
                :key="`selected-parameters-` + param['mbus address']"
                cols="auto"
                class="shrink pa-1"
              >
                <v-menu :close-on-content-click="false">
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      closable
                      color="primary"
                      @click:close="removeQuickItem(param['mbus address'])"
                    >
                      {{ param.name }}
                    </v-chip>
                  </template>
                  <v-list>
                    <v-list-item>
                      <v-row class="pa-5">
                        <v-card>
                          <v-card-item>
                            <v-card-title>{{ param.name }}</v-card-title>
                            <v-card-subtitle class="mb-2"
                              >Parameter Name</v-card-subtitle
                            >
                            <v-card-title
                              >{{ param.group }}
                              {{
                                param.sub_group == "0" || param.sub_group == ""
                                  ? ""
                                  : " / " + param.sub_group
                              }}</v-card-title
                            >
                            <v-card-subtitle class="mb-2"
                              >Parameter Group / Subgroup</v-card-subtitle
                            >
                            <v-card-title>{{ param.user_info }}</v-card-title>
                            <v-card-subtitle class="mb-2"
                              >Parameter Info</v-card-subtitle
                            >
                          </v-card-item>
                          <v-card-actions>
                            <v-btn
                              variant="outlined"
                              @click="removeQuickItem(param['mbus address'])"
                            >
                              Remove Parameter
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-row>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
          </template>
          <v-row>
            <v-col>
              <v-text-field
                v-model="searchTerm"
                label="Search term for parameters"
                variant="underlined"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="selectedGroups"
                :items="groups"
                label="Group"
                variant="underlined"
                multiple
              />
            </v-col>
          </v-row>
          <v-list
            v-scroll.self="onScroll"
            class="overflow-y-auto"
            max-height="400"
          >
            <v-list-item
              v-for="item in items"
              :key="'key-' + item['mbus address']"
            >
              <ParameterSelectItem :item="item" />
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-btn>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useModbusStore } from "../stores/useModbusStore";
import ParameterSelectItem from "./ParameterSelectItem.vue";
import { ParamField } from "../enums/ParamField";
import { ModbusParam } from "@/types/ModbusParam";

const modbusStore = useModbusStore();

const dialog = ref(false);
const searchTerm = ref("");

const quickParams = computed<ModbusParam[]>(() => {
  return modbusStore.quickParams.filter(
    (param): boolean =>
      modbusStore.selectedQuickParams[param[ParamField.mbusAddress]],
  );
});
const hasQuickParams = computed<boolean>(() => quickParams.value.length > 0);

watch(quickParams, (newVal) => {
  const addr_arr = newVal.map((item) => item[ParamField.mbusAddress]);
  localStorage.setItem(
    "charts_page" + modbusStore.portIndex.toString() + "/quickparams",
    JSON.stringify(addr_arr),
  );
});
const selectedGroups = ref([]);
const groups = computed(() => {
  return modbusStore.groups;
});

const items = computed(() => {
  if (selectedGroups.value.length == 0) {
    return modbusStore.quickParams.filter(
      (item) =>
        item.name.toUpperCase().indexOf(searchTerm.value.toUpperCase()) > -1,
    );
  } else {
    return modbusStore.quickParams.filter(
      (param) =>
        selectedGroups.value.find((item) => item == param.group) != undefined &&
        param.name.toUpperCase().indexOf(searchTerm.value.toUpperCase()) > -1,
    );
  }
});

const scrollInvoked = ref(0);
function onScroll() {
  scrollInvoked.value++;
}

function removeQuickItem(mbus_address: string) {
  modbusStore.selectedQuickParams[mbus_address] = false;
}
</script>
